<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import {
  Table,
  TableIconText,
  UserAvatar,
  ButtonType,
  CustomButton,
  Header,
  TableColumnType,
  FormDeleteAlertDialog
} from '@/components';
import { ArchiveModel, ArchiveType, DELETE_CONFIRMATION_TYPE } from '@/models';
import { formattedDate, DATE_FORMAT_2, routeConstants } from '@/utils';
import { useArchiveStore } from '@/stores/archive';
import { useEntryListStore } from '@/stores/entryList';

const archiveStore = useArchiveStore();
const entryListStore = useEntryListStore();
archiveStore.getArchivedCases();

const selectedRows = ref<ArchiveModel[]>([]);

const router = useRouter();
const onItemClick = (archive: ArchiveModel) => {
  if (archive.type === ArchiveType.CASE) {
    entryListStore.selectedCase = archive.caseItem;
    router.push({
      name: routeConstants.CASE.name,
      params: {
        identifier: archive.identifier
      }
    });
  } else if (archive.type === ArchiveType.ENTRY) {
    router.push({
      name: routeConstants.ENTRY.name,
      params: {
        formIdentifier: archive.entry?.formIdentifier,
        entryIdentifier: archive.entry?.identifier
      }
    });
  }
};

//delete confirmation
const showDeleteAlert = ref<boolean>(false);
const openCloseDeleteAlert = () => {
  showDeleteAlert.value = !showDeleteAlert.value;
};
const dataAction = async (status: string) => {
  await archiveStore.restoreResources(selectedRows.value, status);
  selectedRows.value = [];
  if (status === 'delete') {
    openCloseDeleteAlert();
  }
};
</script>

<template>
  <div class="archive-section">
    <div class="archive-wrapper">
      <div class="header">
        <Header title="Archive">
          <template #headerButtons>
            <div class="header-buttons" v-if="selectedRows.length > 0">
              <CustomButton text="Delete"
                :type="ButtonType.NEGATIVE"
                :margin-left="16"
                @onClick="openCloseDeleteAlert"
                :icon="{ icon: 'trash', style: 'fal' }"/>

              <CustomButton text="Restore"
                :type="ButtonType.OUTLINED"
                :margin-left="16"
                @onClick="dataAction('active')"
                :icon="{ icon: 'arrow-rotate-left', style: 'far' }"/>
            </div>
          </template>
        </Header>
      </div>

      <div class="archive-body" v-if="archiveStore.archives">
        <Table :columns="archiveStore.tableColumns"
          :dataList="archiveStore.archives"
          :loading-state="archiveStore.archiveLoader.isLoading"
          :selection="true"
          v-model:selectedRows="selectedRows"
          @onRowClick="onItemClick"
          presort="archivedAt">
          <template #bodyContent="{ data, col }">
            <div class="case-item"
              v-if="col.type === TableColumnType.TEXT_WTIH_ICON">
              <TableIconText :text="data[col.key]" :icon="data['icon']" />
              <FontAwesomeIcon :icon="[`fas`, `lock`]"
                v-if="data['lockedAt']"
                class="lock"/>
            </div>

            <div v-if="col.type === TableColumnType.TEXT">
              {{
                col.key === "createdAt" || col.key === "archivedAt"
                  ? formattedDate(data[col.key], DATE_FORMAT_2)
                  : data[col.key]
              }}
            </div>

            <UserAvatar :avatarUrl="data.creatorAvatar"
              :userName="data[col.key] ?? 'System Generated'"
              :size="28"
              v-if="col.type === TableColumnType.USER"/>
          </template>
          <template #emptyContent>
            <div class="empty-content-container">
              <div class="empty-content">
                <FontAwesomeIcon :icon="[`far`, 'box-archive']" class="archive-icon" />
                <h3 class="no-entries-label">No data in archive</h3>
              </div>
            </div>
          </template>
        </Table>
      </div>
    </div>
  </div>

  <FormDeleteAlertDialog v-if="showDeleteAlert"
    :visible="showDeleteAlert"
    :isLoading="archiveStore.archiveLoader.isLoading"
    :confirmationType="DELETE_CONFIRMATION_TYPE.ARCHIVE"
    @onClose="openCloseDeleteAlert"
    @onDelete="dataAction('delete')"/>
</template>

<style scope lang="scss">
.archive-section {
  padding: 48px 100px 40px 80px;
  height: 100%;
}
.archive-wrapper {
  max-width: 1100px;
  height: 100%;
  display: flex;
  flex-direction: column;
}
.header-buttons {
  display: flex;
}
.p-row-even, .p-row-odd {
  cursor: pointer;
}
.empty-content-container {
  padding: 50px 0px;
  display: flex;
  justify-content: center;
  align-items: center;
  .empty-content {
    text-align: center;
    .archive-icon {
      font-size: 100px;
      margin: 16px 0px;
      color: #ced3d9;
    }

    .no-entries-label {
      font-weight: bold;
    }

    .new-entry {
      margin: 16px 0px;
      display: flex;
      align-items: center;
    }
  }
}
</style>
